import React from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dumbbell, Edit, Plus, Search, Timer, Trash2, Video } from "lucide-react";

// Exercise library CRUD.
const ExercisesTab = ({
  deleteExercise,
  filteredExercises,
  searchExercises,
  setEditingExercise,
  setExerciseOpen,
  setGifPreview,
  setNewExercise,
  setSearchExercises,
  equipmentFilter,
  setEquipmentFilter,
  equipmentOptions,
  muscleGroupFilter,
  setMuscleGroupFilter,
  muscleGroupOptions,
  levelFilter,
  setLevelFilter,
  levelOptions,
  mechanicFilter,
  setMechanicFilter,
  mechanicOptions,
}) => {
  return (
    <div className="exercises-view">
      <div className="view-header">
        <h1>Biblioteka ćwiczeń</h1>
        <div style={{display:'flex', gap:'8px', alignItems:'center', flexWrap: 'wrap'}}>
          <div style={{position:'relative'}}>
            <Search size={16} style={{position:'absolute',left:8,top:'50%',transform:'translateY(-50%)',color:'var(--text-secondary)'}} />
            <Input placeholder="Szukaj..." value={searchExercises} onChange={e => setSearchExercises(e.target.value)} style={{paddingLeft:32,width:200}} />
          </div>
          <Select value={equipmentFilter || "all"} onValueChange={(v) => setEquipmentFilter(v === "all" ? "" : v)}>
            <SelectTrigger style={{ width: 160 }} data-testid="equipment-filter"><SelectValue placeholder="Sprzęt" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Sprzęt (wszystkie)</SelectItem>
              {(equipmentOptions || []).map(eq => <SelectItem key={eq} value={eq}>{eq}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={muscleGroupFilter || "all"} onValueChange={(v) => setMuscleGroupFilter(v === "all" ? "" : v)}>
            <SelectTrigger style={{ width: 180 }} data-testid="muscle-group-filter"><SelectValue placeholder="Partia mięśniowa" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Partia mięśniowa (wszystkie)</SelectItem>
              {(muscleGroupOptions || []).map(mg => <SelectItem key={mg} value={mg}>{mg}</SelectItem>)}
            </SelectContent>
          </Select>
          {/* Poziom/mechanika ruchu — tylko część ćwiczeń (Free Exercise DB) je ma;
              te z ExerciseDB po prostu nie pasują do filtra, jeśli jest ustawiony. */}
          <Select value={levelFilter || "all"} onValueChange={(v) => setLevelFilter(v === "all" ? "" : v)}>
            <SelectTrigger style={{ width: 170 }} data-testid="level-filter"><SelectValue placeholder="Poziom" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Poziom (wszystkie)</SelectItem>
              {(levelOptions || []).map(l => <SelectItem key={l} value={l}>{l}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={mechanicFilter || "all"} onValueChange={(v) => setMechanicFilter(v === "all" ? "" : v)}>
            <SelectTrigger style={{ width: 170 }} data-testid="mechanic-filter"><SelectValue placeholder="Typ ruchu" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Typ ruchu (wszystkie)</SelectItem>
              {(mechanicOptions || []).map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button onClick={() => { setEditingExercise(null); setExerciseOpen(true); }} data-testid="add-exercise-btn">
            <Plus size={18} /> Nowe ćwiczenie
          </Button>
        </div>
      </div>

      {filteredExercises.length === 0 ? (
        <Card className="empty-state">
          <CardContent>
            <Dumbbell size={48} strokeWidth={1} />
            <h3>Brak ćwiczeń</h3>
            <p>Dodaj ćwiczenia aby tworzyć plany treningowe</p>
          </CardContent>
        </Card>
      ) : (
        <div className="exercises-grid">
          {filteredExercises.map(ex => (
            <Card key={ex.id} className="exercise-card clickable-card" data-testid={`exercise-${ex.id}`} onClick={() => { setEditingExercise(ex); setNewExercise({...ex}); setExerciseOpen(true); }}>
              <div className="exercise-card-actions">
                <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); setEditingExercise(ex); setNewExercise({...ex}); setExerciseOpen(true); }} data-testid={`edit-${ex.id}`}>
                  <Edit size={14} />
                </Button>
                <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); deleteExercise(ex.id); }} data-testid={`delete-${ex.id}`}>
                  <Trash2 size={14} />
                </Button>
              </div>
              <CardContent>
                {ex.image_url && (
                  <img src={ex.image_url} alt={ex.name} loading="lazy" title="Kliknij, aby powiększyć"
                    onClick={(e) => { e.stopPropagation(); setGifPreview({ url: ex.image_url, name: ex.name }); }}
                    style={{ width: "100%", height: 130, objectFit: "contain", background: "#f6f6f6", borderRadius: 8, marginBottom: 8, cursor: "zoom-in" }} />
                )}
                <Badge variant="outline">{ex.category}</Badge>
                {ex.level && <Badge variant="secondary" style={{ marginLeft: 6 }}>{ex.level}</Badge>}
                <h3>{ex.name}</h3>
                {ex.description && <p>{ex.description}</p>}
                {ex.tabata_work && (
                  <div className="tabata-badge">
                    <Timer size={12} /> {ex.tabata_work}s/{ex.tabata_rest}s x{ex.tabata_rounds}
                  </div>
                )}
                {ex.video_url && (
                  <a href={ex.video_url} target="_blank" rel="noopener noreferrer" className="video-link">
                    <Video size={14} /> Video
                  </a>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default ExercisesTab;
